<script setup>
import OutlinedSheet from '@/Components/OutlinedSheet.vue';
import { useVModel } from '@vueuse/core';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps({
    modelValue: {
        type: String,
        default: '',
    },
    label: {
        type: String,
        default: null,
    },
    errorMessages: {
        type: [Array, String],
        default: () => [],
    },
    height: {
        type: Number,
        default: 220,
    },
});

const emit = defineEmits(['update:modelValue']);

const value = useVModel(props, 'modelValue', emit);
const canvas = ref(null);
const canvasWrapper = ref(null);
const drawing = ref(false);
const hasSigned = computed(() => value.value.length > 0);

let resizeObserver = null;

const getContext = () => {
    return canvas.value?.getContext('2d');
};

const prepareCanvas = () => {
    if (!canvas.value || !canvasWrapper.value) {
        return;
    }

    const width = canvasWrapper.value.clientWidth;
    const height = props.height;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const currentImage = value.value;

    canvas.value.width = Math.floor(width * ratio);
    canvas.value.height = Math.floor(height * ratio);
    canvas.value.style.width = `${width}px`;
    canvas.value.style.height = `${height}px`;

    const context = getContext();

    if (!context) {
        return;
    }

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.scale(ratio, ratio);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.lineWidth = 2;
    context.strokeStyle = '#111827';

    if (currentImage) {
        const image = new Image();
        image.onload = () => {
            context.drawImage(image, 0, 0, width, height);
        };
        image.src = currentImage;
    }
};

const eventPosition = (event) => {
    const rect = canvas.value.getBoundingClientRect();

    return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
    };
};

const startDrawing = (event) => {
    if (!canvas.value) {
        return;
    }

    const context = getContext();
    if (!context) {
        return;
    }
    const position = eventPosition(event);

    drawing.value = true;
    canvas.value.setPointerCapture(event.pointerId);
    context.beginPath();
    context.moveTo(position.x, position.y);
};

const draw = (event) => {
    if (!drawing.value) {
        return;
    }

    const context = getContext();
    if (!context) {
        return;
    }
    const position = eventPosition(event);

    context.lineTo(position.x, position.y);
    context.stroke();
};

const stopDrawing = (event) => {
    if (!drawing.value || !canvas.value) {
        return;
    }

    draw(event);
    drawing.value = false;
    value.value = canvas.value.toDataURL('image/png');
};

const clear = () => {
    value.value = '';
    prepareCanvas();
};

onMounted(() => {
    prepareCanvas();

    resizeObserver = new ResizeObserver(() => {
        prepareCanvas();
    });

    if (canvasWrapper.value) {
        resizeObserver.observe(canvasWrapper.value);
    }
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
});
</script>

<template>
    <div class="tw-mb-2">
        <OutlinedSheet>
            <template #title>
                {{ label }}
            </template>
            <div ref="canvasWrapper" class="tw-relative">
                <canvas
                    ref="canvas"
                    class="tw-w-full tw-rounded-lg tw-border tw-border-dashed tw-border-slate-300 tw-bg-white"
                    :style="{ height: `${height}px`, touchAction: 'none' }"
                    @pointerdown="startDrawing"
                    @pointermove="draw"
                    @pointerup="stopDrawing"
                    @pointerleave="stopDrawing"
                    @pointercancel="stopDrawing"
                />
                <div
                    v-if="!hasSigned"
                    class="tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-text-sm tw-text-slate-400"
                >
                    {{ $t('Sign here with your finger') }}
                </div>
            </div>
            <div class="tw-mt-3 tw-flex tw-items-center tw-justify-between">
                <div class="tw-text-sm tw-text-slate-500">
                    {{ $t('Use one finger to sign inside the box.') }}
                </div>
                <v-btn variant="text" color="primary" @click="clear">
                    {{ $t('Clear') }}
                </v-btn>
            </div>
        </OutlinedSheet>
        <v-messages
            v-if="
                Array.isArray(errorMessages)
                    ? errorMessages.length
                    : errorMessages
            "
            color="error"
            :messages="errorMessages"
        />
    </div>
</template>
